import { DndContext } from "@dnd-kit/core"
import type { DragEndEvent } from "@dnd-kit/core"
import { SortableContext, arrayMove } from "@dnd-kit/sortable"
import { Box, Button, Paper } from "@material-ui/core"
import Add from "@material-ui/icons/Add"
import Close from "@material-ui/icons/Close"
import Save from "@material-ui/icons/Save"
import produce from "immer"
import type { ReactElement } from "react"
import { useEffect, useMemo, useState } from "react"
import { connect } from "react-redux"

import type { Metadata as FeedbackMetadata } from "@infinitaslearning/module-il-feedback-widget"
import type { IProjectEditDialogProps, IProjectPreviewDialogProps } from ".."
import { AssignmentDialog, MapSelector, ProjectEditDialog, ProjectPreviewDialog } from ".."
import { config } from "../../../config"
import { useBrandName, useCallbackSafeProps, useIsOwner, useIsSafeExamBrowser, useStateMap } from "../../../hooks"
import type { MapDetailDto, UserGroupDto } from "../../../models"
import { ComparisonLayout, FeatureFlags } from "../../../models"
import {
  comparisonCopy,
  comparisonSave,
  comparisonSetProjects,
  comparisonUpdate,
  pushMessage,
  shareComparisonWithGroup
} from "../../../redux"
import type {
  AppState,
  AppThunkDispatch,
  Bundle,
  Comparison,
  ComparisonActions,
  MapAction,
  Project,
  SnackbarAction
} from "../../../redux"
import { history } from "../../../redux"
import { isColumnsOnly, isReverse, isVertical } from "../../../utils"
import { Body1, Header4, Header5 } from "../../atoms"
import {
  BackButton,
  ComparisonMoreOptions,
  ComparisonProject,
  DialogWindow,
  UnsavedProjectDialog
} from "../../molecules"

import "./comparison-viewer.styles.scss"
import { useFeatureFlag } from "configcat-react"
import { useTranslation } from "react-i18next"
import { ContentFeedback } from "../../molecules/content-feedback/content-feedback.component"
import { ShareDialog } from "../share-dialog/share-dialog.component"
import type { IShareDialogProps } from "../share-dialog/share-dialog.component"

export interface IComparisonViewerProps {
  comparison: Comparison
  onRemoveProject?: (index: number) => void
  reorderProjects?: (projects: Project[]) => void
}

export interface IComparisonViewerStateProps {
  selectedProjects: Project[]
  isDirty: boolean
  getDispatch: () => AppThunkDispatch<MapAction>
  setProjects: (projects: Project[]) => void
  saveComparison: (comparison: Comparison, brandName: string, navigateBack?: boolean) => void
  copyComparison: (comparison: Comparison, brandName: string) => void
  updateComparison: (comparison: Comparison) => void
  pushMessage: (message: string) => void
  shareComparison: (groups: UserGroupDto[], comparison: Comparison) => void
}

function ComparisonViewerView(props: IComparisonViewerProps & IComparisonViewerStateProps): ReactElement {
  const {
    comparison,
    selectedProjects,
    onRemoveProject,
    reorderProjects,
    getDispatch,
    setProjects,
    saveComparison,
    copyComparison,
    updateComparison,
    pushMessage,
    shareComparison,
    isDirty
  } = props

  const { t } = useTranslation(["general", "comparison"])

  const isSafeExamBrowser = useIsSafeExamBrowser()
  const brandName: string = useBrandName()
  const isComparisonOwner = useIsOwner(comparison)
  const { value: isFeedbackButtonVisible } = useFeatureFlag(FeatureFlags.feedback, false)

  const projectCount: number = comparison.projects.length
  const [editMaps, setEditMaps] = useState<boolean>(false)
  const [selectionDialogOpen, setSelectionDialogOpen] = useState<boolean>(false)
  const [getSafeComparison] = useCallbackSafeProps(comparison)

  const [shareDialogState, setShareDialogState] = useState<IShareDialogProps>({
    open: false,
    objectDescriptor: "comparison",
    onClose: closeShareDialog,
    onSave: openSaveDialog,
    onShare: (groups: UserGroupDto[]) => shareComparison(groups, getSafeComparison())
  })

  const [saveDialogState, setSaveDialogState] = useState<IProjectEditDialogProps>({
    open: false,
    dialogTitle: "",
    title: "",
    message: "",
    entityName: "comparison",
    bundles: [],
    onClose: closeSaveDialog,
    onSave: (_: string, __: Bundle[]) => {}
  })
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState<boolean>(false)
  const mainMap: MapDetailDto | undefined = useStateMap(comparison.projects[0].metaDataId, getDispatch())
  const [layout, setLayout] = useState<ComparisonLayout>(comparison.layout ? comparison.layout : getDefaultLayout())
  const [indexesToPrefix, setIndexesToPrefix] = useState<number[]>([])
  const [projectPreviewState, setProjectPreviewState] = useState<IProjectPreviewDialogProps>({
    open: false,
    project: undefined,
    onClose: onClosePreviewDialog
  })
  const [ignoreUnsavedChanges, setIgnoreUnsavedChanges] = useState<boolean>(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const title: string = useMemo<string>(() => {
    if (comparison.title && comparison.title.length > 0) {
      return comparison.title
    }
    return t("general:comparison")
  }, [comparison])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    const addedIds: string[] = []
    comparison.projects.forEach((project: Project, index: number) => {
      if (addedIds.includes(project.metaDataId)) {
        const draft = indexesToPrefix
        draft.push(index)
        setIndexesToPrefix(draft)
      } else {
        addedIds.push(project.metaDataId)
      }
    })
  }, [comparison, selectedProjects])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (projectCount === 1) {
      setEditMaps(true)
      setLayout(ComparisonLayout.Vertical)
    }
  }, [comparison.projects])

  const metadata: FeedbackMetadata = useMemo(() => {
    return {
      atlasComparisonId: comparison.id
    }
  }, [comparison])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (ignoreUnsavedChanges) {
      const project: Project = comparison.projects[0]
      if (project.id) {
        history.push(`/project/${project.id}`)
      } else {
        history.push(`/map/${project.metaDataId}`)
      }
    }
  }, [ignoreUnsavedChanges])

  function openShareDialog(): void {
    setShareDialogState({
      ...shareDialogState,
      open: !shareDialogState.open
    })
  }

  function closeShareDialog(): void {
    setShareDialogState({ ...shareDialogState, open: false })
  }

  function handleCancelClicked(): void {
    if (comparison.projects.length === 1) {
      setIgnoreUnsavedChanges(true)
    } else setEditMaps(false)
  }

  function getDefaultLayout(): ComparisonLayout {
    switch (projectCount) {
      case 2:
        return ComparisonLayout.Vertical
      case 3:
        return ComparisonLayout.VerticalRightSplit
      default:
        return ComparisonLayout.FourWaySplit
    }
  }

  function getGridClassName(): string {
    const count: number = editMaps ? (projectCount < 4 ? projectCount + 1 : projectCount) : projectCount

    switch (count) {
      case 2:
        return `grid-2 ${isVertical(layout) ? "vertical" : "horizontal"}`
      case 3:
        return `grid-3 ${
          isVertical(layout) ? "vertical" : "horizontal"
        } ${isReverse(layout) ? "reverse" : "no-reverse"} ${isColumnsOnly(layout) ? "columns-only" : ""} `
      case 4:
        return "grid-4"
      default:
        return ""
    }
  }

  function getZoomIconsClass(): string {
    const hasThreeOrMore: boolean = comparison.projects.length >= 3
    const isTwoHorizontal: boolean = !isVertical(layout) && comparison.projects.length === 2
    const isThreeVertical: boolean = comparison.projects.length === 3 && isVertical(layout)

    return (hasThreeOrMore || isTwoHorizontal) && !isThreeVertical ? "small-icons" : ""
  }

  function onShowPreview(map: Project): void {
    setProjectPreviewState({
      ...projectPreviewState,
      open: true,
      project: map
    })
  }

  function onClosePreviewDialog(): void {
    setProjectPreviewState({ ...projectPreviewState, open: false })
  }

  function getSortableItems(): string[] {
    return comparison.projects.map((_: Project, index: number) => `${index}`)
  }

  function handleProjectDrag(event: DragEndEvent): void {
    const { active, over } = event

    if (!over || active.id === over.id || !reorderProjects) return

    const newProjects: Project[] = arrayMove(comparison.projects, Number.parseInt(active.id), Number.parseInt(over.id))

    reorderProjects(newProjects)
  }

  function saveProjects(): void {
    setSelectionDialogOpen(false)
    setEditMaps(false)

    setProjects([comparison.projects[0], ...selectedProjects])
  }

  function openSaveDialog(): void {
    setSaveDialogState({
      ...saveDialogState,
      title: title,
      dialogTitle: t("comparison:saveComparison"),
      message: t("comparison:saveComparisonText", { brandName }),
      bundles: comparison.bundles,
      open: true,
      onSave: handleSave
    })
  }

  function openSaveAsDialog(): void {
    setSaveDialogState({
      ...saveDialogState,
      title: title,
      dialogTitle: t("comparison:saveAsComparison"),
      message: t("comparison:saveAsComparisonText", { brandName }),
      bundles: [],
      open: true,
      onSave: handleSaveAs
    })
  }

  function closeSaveDialog(): void {
    setSaveDialogState({
      ...saveDialogState,
      open: false
    })
  }

  function onBackButtonClick(): (() => void) | undefined {
    return comparison.id
      ? undefined
      : comparison.projects[0].id
        ? () => history.push(`project/${comparison.projects[0].id!}`)
        : () => history.push(`map/${comparison.projects[0].metaDataId}`)
  }

  function handleSave(title: string, bundles: Bundle[], navigateBack?: boolean): void {
    const newComparison: Comparison = updateLocalComparison(title, bundles)

    if (!comparison.id) {
      saveComparison(newComparison, brandName, navigateBack)
    } else {
      updateComparison(newComparison)
    }

    closeSaveDialog()
  }

  function handleSaveAs(title: string, bundles: Bundle[]): void {
    const newComparison: Comparison = updateLocalComparison(title, bundles)

    isComparisonOwner ? saveComparison(newComparison, brandName) : copyComparison(newComparison, brandName)
    closeSaveDialog()
  }

  function updateLocalComparison(title: string, bundles: Bundle[]): Comparison {
    return produce(comparison, (draft: Comparison) => {
      draft.title = title
      draft.bundles = bundles
      draft.layout = layout
    })
  }

  function shouldShowUnsavedChangesDialog(): boolean {
    if (!isComparisonOwner) return false
    if (comparison.id === undefined) return true
    return window.localStorage.getItem(config.showUnsavedProjectChangesDialogLocalstorageKey) !== "false"
  }

  function ExtraProjectTemplate(): ReactElement {
    return (
      <Box className="extra-project-template">
        <Header5>{t("comparison:chooseCompare")}</Header5>
        <Body1>{t("comparison:chooseMax3Maps")}</Body1>
        <Button variant="contained" color="primary" onClick={() => setSelectionDialogOpen(true)}>
          <Add />
          {t("comparison:chooseMaps")}
        </Button>
      </Box>
    )
  }

  function handleRemoveComparisonProject(index: number): void {
    if (!onRemoveProject) return
    onRemoveProject(index)
    pushMessage(t("comparison:deletedMap"))
  }

  function getPrefix(index: number): string | undefined {
    if (indexesToPrefix.includes(index)) return `${index}-`
    return
  }

  return (
    <Box className={`comparison-viewer ${editMaps ? "hidden-bar" : ""}`}>
      {editMaps === false && (
        <Box className="comparison-viewer-header">
          <BackButton onClick={onBackButtonClick()} />
          <Header4>{title}</Header4>

          <Box className="comparison-viewer-header-actions">
            {isFeedbackButtonVisible && <ContentFeedback metadata={metadata} />}
            <ComparisonMoreOptions
              comparison={comparison}
              layout={layout}
              onSave={openSaveDialog}
              onSaveAs={openSaveAsDialog}
              onAddMaps={() => setEditMaps(true)}
              onHandIn={() => setAssignmentDialogOpen(true)}
              onSetLayout={(selectedLayout: ComparisonLayout) => setLayout(selectedLayout)}
              onShare={openShareDialog}
            />
            {isSafeExamBrowser || (!isComparisonOwner && comparison.id) ? (
              <Box />
            ) : (
              <Button onClick={openSaveDialog} variant="contained" color="primary">
                <Save /> {t("general:save")}
              </Button>
            )}
          </Box>
        </Box>
      )}
      <DndContext onDragEnd={handleProjectDrag}>
        <SortableContext items={getSortableItems()}>
          <div className={`comparison-viewer-project-container ${getZoomIconsClass()} ${getGridClassName()}`}>
            {comparison.projects.map((project: Project, index: number) => (
              <ComparisonProject
                key={`${index}-${project.metaDataId}`}
                index={index}
                prefix={getPrefix(index)}
                project={project}
                canRemove={projectCount > 1}
                hideControls={editMaps}
                onProjectRemove={handleRemoveComparisonProject}
                onShowPreview={onShowPreview}
              />
            ))}
            {editMaps && projectCount < 4 && <ExtraProjectTemplate />}
          </div>
        </SortableContext>
      </DndContext>
      {editMaps === true && (
        <Paper square elevation={3} className="comparison-viewer-edit-bar">
          <Button color="primary" variant="outlined" onClick={handleCancelClicked}>
            <Close /> {t("general:dialog.cancel")}
          </Button>
          <Button onClick={() => {}} variant="contained" disabled={true}>
            <Add />
            {t("comparison:addToComparison")}
          </Button>
        </Paper>
      )}
      {mainMap !== undefined && <ProjectEditDialog {...saveDialogState} />}
      {editMaps && (
        <DialogWindow open={selectionDialogOpen} fullScreen onClose={() => setSelectionDialogOpen(false)}>
          <MapSelector
            selectedProjects={selectedProjects}
            onConfirm={saveProjects}
            onCancel={() => {
              setSelectionDialogOpen(false)
              if (projectCount > 1) setEditMaps(false)
            }}
            project={comparison.projects[0]}
            comparison={comparison}
          />
        </DialogWindow>
      )}
      <AssignmentDialog
        open={assignmentDialogOpen}
        shouldSave={comparison.id === undefined}
        objectDescriptor="comparison"
        onSave={openSaveDialog}
        onClose={() => setAssignmentDialogOpen(false)}
      />
      {shouldShowUnsavedChangesDialog() && (
        <UnsavedProjectDialog
          isDirty={ignoreUnsavedChanges ? false : isDirty}
          hasId={comparison.id !== undefined}
          onSave={(title, bundles) => handleSave(title, bundles, true)}
          title={title}
          bundles={comparison.bundles}
          entityName="comparison"
        />
      )}
      {projectPreviewState.project && <ProjectPreviewDialog {...projectPreviewState} />}
      <ShareDialog {...shareDialogState} shouldSave={comparison.id === undefined} />
    </Box>
  )
}

function mapStateToProps(state: AppState) {
  return {
    selectedProjects: state.mapSelector.selectedProjects,
    isDirty: state.comparisons.isDirty
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<MapAction | ComparisonActions | SnackbarAction>) {
  return {
    getDispatch: () => dispatch,
    setProjects: (projects: Project[]) => dispatch(comparisonSetProjects(projects)),
    saveComparison: (comparison: Comparison, brandName: string, navigateBack?: boolean) =>
      dispatch(comparisonSave(comparison, brandName, navigateBack)),
    copyComparison: (comparison: Comparison, brandName: string) => dispatch(comparisonCopy(comparison, brandName)),
    updateComparison: (comparison: Comparison) => dispatch(comparisonUpdate(comparison)),
    pushMessage: (message: string) => dispatch(pushMessage(message)),
    shareComparison: (groups: UserGroupDto[], comparison: Comparison) =>
      dispatch(shareComparisonWithGroup(groups, comparison))
  }
}

export const ComparisonViewer = connect(mapStateToProps, mapDispatchToProps)(ComparisonViewerView)
